/*
 * Firestore wiring for the inbox realtime layer. Signals are content-free
 * "something changed for you, refetch" docs; the backend API stays
 * authoritative. Everything here is best-effort: failures never block a send
 * or surface to the user (the 60s poll is the fallback).
 *
 * Doc paths are 4-segment docs nested under the `inbox` collection so the
 * Firestore rule `match /inbox/{document=**}` covers them.
 */
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { firestoreDb } from '../firebase';
import { conversationId, isTypingActive, shouldSendTyping } from './inboxRealtimeHelpers';

export { conversationId, isTypingActive, shouldSendTyping };

const userSignalRef = uuid => doc(firestoreDb, 'inbox', 'signals', 'users', String(uuid));
const customerSignalRef = cid => doc(firestoreDb, 'inbox', 'signals', 'customers', String(cid));
const typingRef = convId => doc(firestoreDb, 'inbox', 'typing', 'conversations', String(convId));

function safeSet(ref, data) {
    try {
        return setDoc(ref, data).catch(() => {});
    } catch (err) {
        return Promise.resolve();
    }
}

function safeSubscribe(ref, handle) {
    try {
        return onSnapshot(ref, handle, () => {});
    } catch (err) {
        return () => {};
    }
}

export function signalUser(uuid, kind = 'message') {
    if (!uuid) { return Promise.resolve(); }
    return safeSet(userSignalRef(uuid), { at: serverTimestamp(), kind });
}

export function signalCustomer(customerId, kind = 'broadcast') {
    if (!customerId) { return Promise.resolve(); }
    return safeSet(customerSignalRef(customerId), { at: serverTimestamp(), kind });
}

export function subscribeUserSignal(uuid, cb) {
    if (!uuid) { return () => {}; }
    return safeSubscribe(userSignalRef(uuid), snap => cb(snap.data() || null));
}

export function subscribeCustomerSignal(customerId, cb) {
    if (!customerId) { return () => {}; }
    return safeSubscribe(customerSignalRef(customerId), snap => cb(snap.data() || null));
}

export function sendTyping(convId, user) {
    if (!convId || !user || !user.uuidv4) { return Promise.resolve(); }
    return safeSet(typingRef(convId), { user: user.uuidv4, name: user.name || '', at: serverTimestamp() });
}

export function subscribeTyping(convId, cb) {
    if (!convId) { return () => {}; }
    return safeSubscribe(typingRef(convId), snap => {
        const d = snap.data();
        if (!d) { cb(null); return; }
        const atMs = d.at && typeof d.at.toMillis === 'function' ? d.at.toMillis() : 0;
        cb({ user: d.user, name: d.name, atMs });
    });
}
